import type { QuickPrefs, QuickSideCard } from '@ehr/shared';

/**
 * ehr#682 (PROGRESS-NOTE-10-20-V141 V129): Show Quick add. Stored as the old Hide Quick add key
 * (`hidden`), so a card someone hid before reads as Show Quick add Off.
 */
export function withShown(p: QuickPrefs, shown: boolean): QuickPrefs {
  const { hidden: _h, ...rest } = p;
  return shown ? rest : { ...rest, hidden: true };
}

/**
 * ehr#682 (V141 V130, V77): what Save for this client / Save for all clients stores. This client:
 * the list and the rail cards switched off become the client's own. All clients: they become the
 * all-clients ones and this client follows them again (other clients' own stay theirs). Show Quick
 * add stays as it was saved (it saves on its own click).
 */
export function quickSaveShape(
  d: QuickPrefs,
  o: {
    to: 'client' | 'all';
    contactId: string;
    ids: string[];
    cards: QuickSideCard[];
    shown: boolean;
  },
): QuickPrefs {
  const { [o.contactId]: _own, ...others } = d.clients;
  const { [o.contactId]: _cards, ...otherCards } = d.clientCards ?? {};
  const next: QuickPrefs =
    o.to === 'client'
      ? {
          ...d,
          clients: { ...d.clients, [o.contactId]: o.ids },
          clientCards: { ...otherCards, [o.contactId]: o.cards },
        }
      : { ...d, onNote: o.ids, clients: others, cardsHidden: o.cards, clientCards: otherCards };
  return withShown(next, o.shown);
}

/** V81 qaUseAll: this client goes back to the all-clients settings (list and rail cards). */
export function allClientsShape(d: QuickPrefs, contactId: string): QuickPrefs {
  const { [contactId]: _own, ...others } = d.clients;
  const { [contactId]: _cards, ...otherCards } = d.clientCards ?? {};
  return { ...d, clients: others, clientCards: otherCards };
}

/**
 * ehr#728 (COLLATERAL_CONTACT_NOTE_10-30-V5 Side cards): what Save for this client / Save for all
 * clients stores on a note kind's own Side cards tab (a Collateral contact note). Only that kind's
 * card switches change: the Quick add lists, the Quick add tab's switches and Show Quick add stay
 * as they are, so nothing reaches the other notes. This client: the switches become the client's
 * own for this kind. All clients: they become this kind's all-clients ones and this client follows
 * them again (other clients' own stay theirs).
 */
export function sideCardsSaveShape(
  d: QuickPrefs,
  o: { to: 'client' | 'all'; contactId: string; kind: string; cards: QuickSideCard[] },
): QuickPrefs {
  const k = d.kindCards?.[o.kind] ?? {};
  const { [o.contactId]: _own, ...others } = k.clients ?? {};
  const next =
    o.to === 'client'
      ? { ...k, clients: { ...others, [o.contactId]: o.cards } }
      : { all: o.cards, ...(Object.keys(others).length ? { clients: others } : {}) };
  return { ...d, kindCards: { ...d.kindCards, [o.kind]: next } };
}

/** ehr#728: Use all-clients settings on a Side cards tab: this client follows this kind's all-clients cards. */
export function sideCardsAllShape(d: QuickPrefs, contactId: string, kind: string): QuickPrefs {
  const k = d.kindCards?.[kind];
  if (!k?.clients?.[contactId]) return d;
  const { [contactId]: _own, ...others } = k.clients;
  const { clients: _c, ...rest } = k;
  return {
    ...d,
    kindCards: {
      ...d.kindCards,
      [kind]: { ...rest, ...(Object.keys(others).length ? { clients: others } : {}) },
    },
  };
}

/** ehr#728: a note kind's own Side cards tab: the kind, and the rail cards it has switches for. */
export interface SideCardsTab {
  kind: string;
  cards: readonly QuickSideCard[];
}
